<template>
  <transition name="app-lock-fade">
    <div v-if="isBlocked" class="device-not-secure-overlay" @wheel.prevent @touchmove.prevent>
      <div class="language-selector">
        <v-select
          :value="currentLocale"
          :items="localeOptions"
          item-text="label"
          item-value="id"
          dense
          hide-details
          outlined
          class="locale-select"
          @change="onLocaleChange"
        />
      </div>
      <v-card flat color="transparent" class="pa-4" max-width="360" width="100%">
        <div class="d-flex justify-center mb-6">
          <v-icon size="64" color="error"> mdi-lock-off </v-icon>
        </div>
        <v-card-title class="text-h2 justify-center text-center">
          {{ $t(`profile.cAppSecurity.${messageKey}.title`) }}
        </v-card-title>
        <v-card-subtitle class="text-body-small mt-2 text-center">
          {{ $t(`profile.cAppSecurity.${messageKey}.body.${isIOS ? 'ios' : 'android'}`) }}
        </v-card-subtitle>
        <v-card-actions v-if="!isIOS && !deviceIsRooted" class="mt-6 justify-center">
          <v-btn color="primary" @click="openSettings">
            {{ $t('profile.cAppSecurity.deviceNotSecure.button') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { App as CapacitorApp } from '@capacitor/app';
import { useCapacitorBiometry } from '@/composables/capacitor/useCapacitorBiometry';
import { useCapacitorRootDetection } from '@/composables/capacitor/useCapacitorRootDetection';
import { useCapacitorStorage } from '@/composables/capacitor/useCapacitorStorage';

const isIOS = Capacitor.getPlatform() === 'ios';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const vm = getCurrentInstance()!.proxy as any;

const localeOptions = [
  { id: 'cs', label: 'Čeština' },
  { id: 'sk', label: 'Slovenčina' },
  { id: 'hu', label: 'Magyar' },
  { id: 'en', label: 'English' },
  { id: 'uk', label: 'Українська' },
  { id: 'ru', label: 'Русский' }
];

const currentLocale = computed(() => vm.$i18n.locale);

function onLocaleChange(locale: string) {
  vm.$i18n.locale = locale;
}

const deviceIsSecure = computed(() => vm.$store.getters['appLock/deviceIsSecure']);
const deviceIsRooted = computed(() => vm.$store.getters['appLock/deviceIsRooted']);
const isBlocked = computed(() => !deviceIsSecure.value || deviceIsRooted.value);
const messageKey = computed(() => (deviceIsRooted.value ? 'deviceRooted' : 'deviceNotSecure'));

async function openSettings() {
  await NativeSettings.open({
    optionAndroid: AndroidSettings.Security,
    optionIOS: IOSSettings.TouchIdPasscode
  }).catch(() => {});
}

let resumeListener: PluginListenerHandle | null = null;

onMounted(async () => {
  if (Capacitor.isNativePlatform()) {
    resumeListener = await CapacitorApp.addListener('resume', async () => {
      const { checkBiometry } = useCapacitorBiometry();
      const { checkRootStatus } = useCapacitorRootDetection();

      const result = await checkBiometry();
      const isRooted = await checkRootStatus();

      const wasSecure = deviceIsSecure.value && !deviceIsRooted.value;
      const nowInsecure = !result.deviceIsSecure || isRooted;

      if (wasSecure && nowInsecure) {
        const { purgeUserData } = useCapacitorStorage();
        await purgeUserData();
      }

      vm.$store.commit('appLock/setDeviceIsSecure', result.deviceIsSecure);
      vm.$store.commit('appLock/setDeviceIsRooted', isRooted);
    });
  }
});

onBeforeUnmount(() => {
  resumeListener?.remove();
});
</script>

<style scoped>
.device-not-secure-overlay {
  position: fixed;
  inset: 0;
  z-index: 210;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: #ffffff;
  -webkit-font-smoothing: antialiased;
}

.language-selector {
  position: absolute;
  top: calc(env(safe-area-inset-top) + 16px);
  right: 16px;
}

.locale-select {
  width: 140px;
}

.app-lock-fade-enter-active,
.app-lock-fade-leave-active {
  transition: opacity 0.2s ease;
}
.app-lock-fade-enter,
.app-lock-fade-leave-to {
  opacity: 0;
}
</style>

